"use client";

import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { usePathname } from "next/navigation";

export default function CustomCursor() {
  const pathname = usePathname();
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  
  // Mouse position
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth spring physics for a "weighty" luxury feel
  const springConfig = { damping: 20, stiffness: 300, mass: 0.5 };
  const cursorX = useSpring(mouseX, springConfig);
  const cursorY = useSpring(mouseY, springConfig);

  // Reset hover state on page navigation
  useEffect(() => {
    setIsHovered(false);
  }, [pathname]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
      if (!isVisible) setIsVisible(true);
    };

    const onMouseDown = () => setIsClicking(true);
    const onMouseUp = () => setIsClicking(false);

    // Robust event delegation for hover detection
    const onMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if target or any parent is interactive
      const interactive = target.closest("a, button, input, textarea, select, [role='button'], .interactive, label");
      setIsHovered(!!interactive);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("mouseover", onMouseOver);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mouseover", onMouseOver);
    };
  }, [mouseX, mouseY, isVisible]);

  // Check for mobile/touch device
  const [isMobile, setIsMobile] = useState(true); // Default true to prevent flash

  useEffect(() => {
    const checkMobile = () => {
      const isTouch = window.matchMedia("(pointer: coarse)").matches;
      const isSmallScreen = window.innerWidth < 1024;
      setIsMobile(isTouch || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Hide system cursor when custom cursor is active
  useEffect(() => {
    if (!isMobile) {
      document.documentElement.classList.add("hide-cursor");
      return () => {
        document.documentElement.classList.remove("hide-cursor");
      };
    }
  }, [isMobile]);

  if (isMobile) return null;

  return (
    <motion.div
      className="fixed top-0 left-0 pointer-events-none z-[99999]"
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ opacity: { duration: 0.3 } }}
      style={{
        translateX: cursorX,
        translateY: cursorY, 
        x: "-50%", 
        y: "-50%",
      }}
    >
      {/* 
        The Cursor Element 
        - Default: Small solid gold dot with white border (High visibility).
        - Hover: Large transparent ring with gold border.
      */}
      <motion.div 
        className="rounded-full flex items-center justify-center backdrop-blur-[1px]" 
        animate={{
            width: isHovered ? 40 : 12,
            height: isHovered ? 40 : 12,
            backgroundColor: isHovered ? "rgba(142, 124, 94, 0.0)" : "#8E7C5E", 
            borderWidth: "1px",
            borderColor: isHovered ? "#8E7C5E" : "rgba(255,255,255,0.8)",
            scale: isClicking ? 0.8 : 1, 
        }}
        transition={{
            type: "spring",
            damping: 25,
            stiffness: 400
        }}
        style={{
          boxShadow: isHovered ? "none" : "0 2px 4px rgba(0,0,0,0.1)"
        }}
      />
    </motion.div>
  );
}
